import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  channelLabel,
  type CampaignVariableField,
  type CampaignVariableMappingEntry,
} from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useChannels } from '../../api/channels';
import { useAuth } from '../../auth/AuthContext';
import { CampaignSendingCard } from './CampaignSendingCard';
import { useWhatsappTemplates } from '../../api/whatsapp-templates';
import { useTags } from '../../api/tags';
import { useContactSegments } from '../../api/contact-segments';
import {
  useCampaign,
  useCreateCampaign,
  useAddCampaignRecipients,
  useStartCampaign,
  useCancelCampaign,
} from '../../api/campaigns';

const FIELD_OPTIONS: { value: CampaignVariableField; label: string }[] = [
  { value: 'displayName', label: 'Display name' },
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'phone', label: 'Phone' },
  { value: 'alternatePhone', label: 'Alternate phone' },
  { value: 'email', label: 'Email' },
  { value: 'companyName', label: 'Company name' },
];

/**
 * Creates a campaign (`/campaigns/new`) and manages an existing one
 * (`/campaigns/:id`) in one page - picking the channel/template/variable
 * mapping happens only at create time; adding recipients, starting, and
 * cancelling happen once the DRAFT campaign exists.
 */
export function CampaignFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (id) return <ManageCampaign campaignId={id} />;
  return <CreateCampaign onCreated={(campaignId) => navigate(`/campaigns/${campaignId}`)} />;
}

function CreateCampaign({ onCreated }: { onCreated: (campaignId: string) => void }) {
  const toast = useToast();
  const channels = useChannels({ type: 'WHATSAPP' });
  const connectedChannels = (channels.data ?? []).filter((c) => c.status === 'CONNECTED');

  const [channelId, setChannelId] = useState('');
  const templates = useWhatsappTemplates({ channelId: channelId || undefined }, Boolean(channelId));
  const marketingTemplates = (templates.data?.items ?? []).filter(
    (t) => t.status === 'APPROVED' && t.category === 'MARKETING',
  );

  const [templateId, setTemplateId] = useState('');
  const template = marketingTemplates.find((t) => t.id === templateId) ?? null;

  const [name, setName] = useState('');
  const [mapping, setMapping] = useState<CampaignVariableMappingEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const create = useCreateCampaign();

  useEffect(() => {
    if (!template) {
      setMapping([]);
      return;
    }
    setMapping((prev) =>
      Array.from(
        { length: template.bodyVariableCount },
        (_, i) => prev[i] ?? { source: 'STATIC', value: '' },
      ),
    );
  }, [template]);

  function setMappingEntry(i: number, entry: CampaignVariableMappingEntry) {
    setMapping((prev) => prev.map((m, idx) => (idx === i ? entry : m)));
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!channelId) errors.channelId = 'Pick a WhatsApp channel.';
    if (!templateId) errors.templateId = 'Pick an approved marketing template.';
    if (name.trim().length < 1) errors.name = 'Enter a name.';
    mapping.forEach((m, i) => {
      if (m.source === 'STATIC' && m.value.trim() === '') {
        errors[`variableMapping.${i}`] = `Variable {{${i + 1}}} needs a value.`;
      }
    });
    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      const campaign = await create.mutateAsync({
        channelId,
        templateId,
        name: name.trim(),
        variableMapping: mapping,
      });
      toast.success('Campaign created.');
      onCreated(campaign.id);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
        }
        setError(err.message);
        toast.error(err.message);
      } else {
        const message = err instanceof Error ? err.message : 'Could not create the campaign.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 700 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>New campaign</h1>
      <Card>
        <form onSubmit={onSubmit} className="ne-auth__form" noValidate>
          <TextField
            label="Campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
          />

          <Select
            label="WhatsApp channel"
            value={channelId}
            options={[
              { value: '', label: channels.isPending ? 'Loading…' : 'Select a channel' },
              ...connectedChannels.map((c) => ({ value: c.id, label: channelLabel(c) })),
            ]}
            onChange={(e) => {
              setChannelId(e.target.value);
              setTemplateId('');
            }}
            error={fieldErrors.channelId}
          />

          <Select
            label="Marketing template"
            value={templateId}
            disabled={!channelId}
            options={[
              {
                value: '',
                label: !channelId
                  ? 'Pick a channel first'
                  : marketingTemplates.length === 0
                    ? 'No approved marketing templates on this channel'
                    : 'Select a template',
              },
              ...marketingTemplates.map((t) => ({
                value: t.id,
                label: `${t.name} (${t.language})`,
              })),
            ]}
            onChange={(e) => setTemplateId(e.target.value)}
            error={fieldErrors.templateId}
          />
          <p
            style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}
          >
            Only APPROVED, MARKETING-category templates can be used - sync templates from{' '}
            <Link to="/channels">Channels</Link> if yours is missing.
          </p>

          {template && template.bodyVariableCount > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <strong>Template variables</strong>
              {mapping.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'end' }}>
                  <span style={{ minWidth: 48 }}>{`{{${i + 1}}}`}</span>
                  <Select
                    label="Source"
                    value={m.source}
                    onChange={(e) =>
                      setMappingEntry(
                        i,
                        e.target.value === 'FIELD'
                          ? { source: 'FIELD', field: 'displayName' }
                          : { source: 'STATIC', value: '' },
                      )
                    }
                    options={[
                      { value: 'FIELD', label: 'Contact field' },
                      { value: 'STATIC', label: 'Fixed text' },
                    ]}
                  />
                  {m.source === 'FIELD' ? (
                    <Select
                      label="Field"
                      value={m.field}
                      onChange={(e) =>
                        setMappingEntry(i, {
                          source: 'FIELD',
                          field: e.target.value as CampaignVariableField,
                        })
                      }
                      options={FIELD_OPTIONS}
                    />
                  ) : (
                    <TextField
                      label="Value"
                      value={m.value}
                      onChange={(e) =>
                        setMappingEntry(i, { source: 'STATIC', value: e.target.value })
                      }
                      error={fieldErrors[`variableMapping.${i}`]}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create campaign'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => void navigateBack()}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );

  function navigateBack() {
    window.history.back();
  }
}

function ManageCampaign({ campaignId }: { campaignId: string }) {
  const location = useLocation();
  const toast = useToast();
  const { data: campaign, isPending, isError, error, refetch } = useCampaign(campaignId);

  const tags = useTags();
  const segments = useContactSegments();
  const addRecipients = useAddCampaignRecipients(campaignId);
  const start = useStartCampaign(campaignId);
  const cancel = useCancelCampaign(campaignId);
  const canManage = useAuth().hasPermission('campaigns.manage');

  const [segmentId, setSegmentId] = useState('');
  const [tagId, setTagId] = useState('');
  const [lastResult, setLastResult] = useState<{
    matchedCount: number;
    eligibleCount: number;
    optedOutExcludedCount: number;
    addedCount: number;
  } | null>(null);

  const prefilledContactIds = useMemo(() => {
    const state = location.state as { contactIds?: string[] } | null;
    return state?.contactIds ?? [];
  }, [location.state]);

  if (isPending) return <PageState kind="loading" message="Loading campaign…" />;
  if (isError) {
    return error.code === 'CAMPAIGN_NOT_FOUND' ? (
      <PageState kind="empty" title="Campaign not found" message="It may have been deleted." />
    ) : (
      <PageState kind="error" message={error.message} onRetry={() => void refetch()} />
    );
  }

  async function runAdd(input: { segmentId?: string; tagId?: string; contactIds?: string[] }) {
    try {
      const result = await addRecipients.mutateAsync(input);
      setLastResult(result);
      toast.success(`${result.addedCount} recipient(s) added.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add recipients.');
    }
  }

  async function onStart() {
    try {
      await start.mutateAsync();
      toast.success('Campaign queued - it will send on the next worker run.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start the campaign.');
    }
  }

  async function onCancel() {
    if (!window.confirm('Cancel this campaign? Remaining recipients will not be sent to.')) return;
    try {
      await cancel.mutateAsync();
      toast.success('Campaign cancelled.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel the campaign.');
    }
  }

  const c = campaign;
  const canManageRecipients = c.status === 'DRAFT' || c.status === 'QUEUED';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{c.name}</h1>
        <span className="ne-badge">{c.status}</span>
      </div>

      <Card title="Recipients">
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-3)',
          }}
        >
          <span className="ne-badge">{c.totalRecipients} total</span>
          <span className="ne-badge">{c.recipientCounts.pending} pending</span>
          <span className="ne-badge">{c.recipientCounts.sent} sent</span>
          <span className="ne-badge">{c.recipientCounts.failed} failed</span>
          <span className="ne-badge">{c.recipientCounts.skippedOptOut} opted-out skipped</span>
          <span className="ne-badge">{c.recipientCounts.skippedInvalid} invalid skipped</span>
        </div>

        {canManageRecipients ? (
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {prefilledContactIds.length > 0 ? (
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <span>{prefilledContactIds.length} contact(s) selected from Contacts.</span>
                <Button
                  disabled={addRecipients.isPending}
                  onClick={() => void runAdd({ contactIds: prefilledContactIds })}
                >
                  Add these
                </Button>
              </div>
            ) : null}

            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'end',
                flexWrap: 'wrap',
              }}
            >
              <Select
                label="Saved segment"
                value={segmentId}
                options={[
                  { value: '', label: 'None' },
                  ...(segments.data ?? []).map((s) => ({ value: s.id, label: s.name })),
                ]}
                onChange={(e) => setSegmentId(e.target.value)}
              />
              <Button
                variant="secondary"
                disabled={!segmentId || addRecipients.isPending}
                onClick={() => void runAdd({ segmentId })}
              >
                Add from segment
              </Button>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'end',
                flexWrap: 'wrap',
              }}
            >
              <Select
                label="Tag"
                value={tagId}
                options={[
                  { value: '', label: 'None' },
                  ...(tags.data ?? []).map((t) => ({ value: t.id, label: t.name })),
                ]}
                onChange={(e) => setTagId(e.target.value)}
              />
              <Button
                variant="secondary"
                disabled={!tagId || addRecipients.isPending}
                onClick={() => void runAdd({ tagId })}
              >
                Add from tag
              </Button>
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
            Recipients can only be changed while the campaign is DRAFT or QUEUED.
          </p>
        )}

        {lastResult ? (
          <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--font-size-sm)' }}>
            Matched {lastResult.matchedCount} · eligible {lastResult.eligibleCount} · excluded
            (opted out) {lastResult.optedOutExcludedCount} · added {lastResult.addedCount}
          </p>
        ) : null}
      </Card>

      {c.status !== 'CANCELLED' || c.sentCount > 0 ? (
        <CampaignSendingCard campaign={c} canManage={canManage} />
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        {c.status === 'DRAFT' ? (
          <Button
            disabled={c.totalRecipients === 0 || start.isPending}
            onClick={() => void onStart()}
          >
            {start.isPending ? 'Starting…' : 'Start campaign'}
          </Button>
        ) : null}
        {c.status === 'QUEUED' || c.status === 'SENDING' ? (
          <Button variant="secondary" disabled={cancel.isPending} onClick={() => void onCancel()}>
            Cancel campaign
          </Button>
        ) : null}
      </div>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/campaigns">← All campaigns</Link>
      </p>
    </div>
  );
}
